import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import { FadeUp, MaskedLine, Eyebrow, DrawLine, ParallaxImg } from "@/components/Reveal";
import QuoteCTA from "@/components/QuoteCTA";

const KHALID_PORTRAIT = "https://customer-assets-7cd3h4nn.emergentagent.net/job_elite-transfer-italy/artifacts/cboc7dja_IMG-20260521-WA0041.jpg";
const CHAPTERS = [
  { n: "01", t: "The client at the centre", d: "Zaichauffer was founded with one simple goal: to offer a private chauffeur service built on putting the client at the centre of every journey." },
  { n: "02", t: "A direct relationship", d: "Khalid El Aissaoui personally oversees the service, keeping a direct relationship with the client and taking care of every detail of the transfer." },
  { n: "03", t: "Every journey, an experience", d: "Whether it is a business trip, an airport transfer, an event or a private journey, the goal is an experience defined by punctuality, comfort and discretion." },
];

export default function ChiSiamoEn() {
  return (
    <>
      <SEO
        title="About us | Zaichauffer – Private Chauffeur in Italy"
        description="Zaichauffer is the private chauffeur service run directly by Khalid El Aissaoui: a direct relationship, no middlemen, for transfers throughout Italy."
      />
      <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 pb-20 pt-40 md:pb-28 md:pt-52" data-testid="chi-siamo-hero">
      <ShieldWm className="-z-10 -right-24 top-10 w-[400px] opacity-[0.04]" />
        <FadeUp><Eyebrow>About us</Eyebrow></FadeUp>
        <h1 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
          <MaskedLine>PERSONALITY, PROFESSIONALITY,</MaskedLine>
          <MaskedLine delay={0.15}>DISCRETION.</MaskedLine>
        </h1>
        <DrawLine className="mt-10" />
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-36" data-testid="founder-section">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp className="lg:sticky lg:top-32">
              <div className="frame-gold">
                <ParallaxImg src={KHALID_PORTRAIT} alt="Khalid El Aissaoui, owner and chauffeur of Zaichauffer, next to his vehicle" className="relative z-10 aspect-[4/5] w-full" />
              </div>
              <p className="label-cap mt-6 !text-[9px]">Khalid El Aissaoui — Owner &amp; Chauffeur</p>
            </FadeUp>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-0">
              <div className="pb-12">
                <Eyebrow>Your driver</Eyebrow>
                <h2 className="mt-8 font-serif text-2xl text-bone md:text-3xl">Khalid, your chauffeur.</h2>
                <p className="mt-5 max-w-xl text-[15px] font-light leading-relaxed text-mute">
                  The owner himself: Khalid El Aissaoui personally accompanies
                  every client, taking care of every detail of the transfer
                  with the utmost punctuality and discretion, in Italy and
                  across Europe.
                </p>
                <a
                  href="https://wa.me/393208189469"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lux-link group mt-8 inline-flex"
                  data-testid="founder-whatsapp-link"
                >
                  Message Khalid on WhatsApp
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
              {CHAPTERS.map((c, i) => (
                <FadeUp key={c.n} delay={0.05 * i}>
                  <div className="border-t border-white/10 py-12">
                    <div className="flex flex-col gap-6 md:flex-row md:items-baseline md:gap-12">
                      <span className="font-serif text-3xl italic text-gold/80">{c.n}</span>
                      <div className="max-w-xl">
                        <h2 className="font-serif text-2xl text-bone md:text-3xl">{c.t}</h2>
                        <p className="mt-5 text-[15px] font-light leading-relaxed text-mute">{c.d}</p>
                      </div>
                    </div>
                  </div>
                </FadeUp>
              ))}
              <div className="border-t border-white/10" />
            </div>

            <FadeUp delay={0.1}>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link to="/en/get-a-quote" className="btn-solid group">
                  Request a quote
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/en/contacts" className="btn-outline">Speak directly to Khalid</Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <QuoteCTA
        title="ALREADY HAVE A DESTINATION?"
        text="Tell us where you need to be and when. We will reply with a tailored solution."
        button="Request your quote"
        to="/en/get-a-quote"
      />
    </>
  );
}
